import apiClient from "./apiClient";
import { BACKEND_URL } from "@/config/api";
import { isGlobalOwner } from "@/lib/permissions";

// Token storage keys
const TOKEN_KEY = "jwt_token";
const USER_KEY = "user_info";
// Empresa escolhida pelo owner global (enviada em X-Company-Code)
const SELECTED_COMPANY_KEY = "selected_company_code";

class AuthService {
  /**
   * Autentica o usuário e guarda o token.
   * Os dados completos (roles, empresa) vêm em seguida de getCurrentUser().
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{token: string, token_type: string, expires_in_ms: number}>}
   */
  async login(email, password) {
    try {
      // Limpa a sessão anterior: o token antigo não pode ir junto
      this.logout();

      const response = await apiClient.post(
        "/api/auth/login",
        { email, password },
        { silent: true, skipAuth: true },
      );

      // Backend pode retornar 'token' ou 'access_token'
      const access_token = response.data?.access_token || response.data?.token;
      if (!access_token) {
        console.error("Erro: Nenhum token retornado pelo servidor");
        throw new Error("No access token received from server");
      }

      this.setToken(access_token);
      return response.data;
    } catch (error) {
      if (error.response) {
        console.error(
          "Erro no login:",
          error.response.status,
          error.response.data,
        );
      } else if (error.request) {
        console.error(
          "Erro: Backend não respondeu. Verifique se está rodando em:",
          BACKEND_URL,
        );
      } else {
        console.error("Erro ao processar login:", error.message);
      }

      throw error;
    }
  }

  /**
   * Busca o usuário atual (GET /api/auth/me) e completa com a empresa do token.
   * @returns {Promise<{id, email, username, roles, role, company_code}>}
   */
  async getCurrentUser() {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error("No token found");
      }

      const response = await apiClient.get("/api/auth/me", { silent: true });

      // Backend retorna: {"id":1,"email":"...","roles":["..."]}
      const userData = {
        id: response.data.id,
        email: response.data.email,
        username: response.data.email, // Compatibilidade
        roles: response.data.roles || [],
        role: response.data.roles?.[0] || "", // Primeiro role como padrão
        // /api/auth/me não devolve a empresa; ela vem no claim company_code do JWT
        company_code: this.getTokenCompanyCode(token),
      };

      this.setUserInfo(userData);

      // Empresa escolhida só vale para o owner global
      if (!isGlobalOwner(userData)) {
        this.setSelectedCompanyCode(null);
      }

      return userData;
    } catch (error) {
      if (error.response?.status === 401) {
        console.warn("⚠️ Token inválido ou expirado");
      } else {
        console.error(
          "Erro ao validar token:",
          error.response?.status || error.message,
        );
      }
      this.logout();
      throw error;
    }
  }

  /**
   * Encerra a sessão e limpa os dados locais da empresa
   */
  logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(SELECTED_COMPANY_KEY);
    localStorage.removeItem("printOrder");
  }

  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  setToken(token) {
    localStorage.setItem(TOKEN_KEY, token);
  }

  getUserInfo() {
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return null;

    try {
      return JSON.parse(userStr);
    } catch (error) {
      console.error("Error parsing user info:", error);
      return null;
    }
  }

  setUserInfo(userInfo) {
    localStorage.setItem(USER_KEY, JSON.stringify(userInfo));
  }

  /**
   * Empresa escolhida pelo owner global (null = todas as empresas)
   * @returns {string|null}
   */
  getSelectedCompanyCode() {
    return localStorage.getItem(SELECTED_COMPANY_KEY) || null;
  }

  setSelectedCompanyCode(code) {
    if (code) {
      localStorage.setItem(SELECTED_COMPANY_KEY, code);
    } else {
      localStorage.removeItem(SELECTED_COMPANY_KEY);
    }
  }

  /**
   * Header X-Company-Code: só para o owner global com empresa escolhida.
   * Para os demais perfis a empresa vem do token (o header seria ignorado).
   * @returns {string|null}
   */
  getCompanyScopeHeader() {
    const selected = this.getSelectedCompanyCode();
    if (!selected) return null;
    return isGlobalOwner(this.getUserInfo()) ? selected : null;
  }

  isAuthenticated() {
    return !!this.getToken();
  }

  /**
   * Claim company_code do JWT (ausente só no owner global)
   * @param {string} token
   * @returns {string|null}
   */
  getTokenCompanyCode(token = this.getToken()) {
    if (!token) return null;
    return this.decodeToken(token)?.company_code || null;
  }

  /**
   * Decode JWT token (basic decode without verification)
   * @param {string} token
   * @returns {object|null}
   */
  decodeToken(token) {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  }

  isTokenExpired(token) {
    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) return true;

    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  }

  isTokenValid() {
    const token = this.getToken();
    if (!token) return false;

    return !this.isTokenExpired(token);
  }
}

// Export singleton instance
const authService = new AuthService();
export default authService;
